import React from "react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

interface BubbleData {
  id: string;
  label: string;
  value: number;
  x: number;
  y: number;
}

interface BubbleChartProps {
  data: BubbleData[];
  title: string;
}

export const generateHSLGradient = (index: number) => {
  const baseHue = (94 + index * 60) % 360;
  const startColor = `hsl(${baseHue}, 80%, 55%)`;
  const endColor = `hsl(${baseHue}, 80%, 35%)`;

  return {
    id: `gradient-${index}`,
    startColor,
    endColor,
  };
};

const WCABubbleChart: React.FC<BubbleChartProps> = ({ data, title }) => {
  // Custom scatter point (bubble) component
  const CustomShape = (props: any) => {
    const { cx, cy, payload } = props;
    const { startColor } = generateHSLGradient(
      data.findIndex((item) => item.id === payload.id)
    );
    const size = (payload.value / Math.max(...data.map((d) => d.value))) * 60;

    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={size / 2}
          fill={startColor}
          className="transition-all duration-300 hover:opacity-80"
        />
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          fill="#fff"
          fontSize="12"
          dy="-0.5em"
        >
          {payload.label}
        </text>
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          fill="#fff"
          fontSize="10"
          dy="1em"
          opacity="0.8"
        >
          ({payload.value})
        </text>
      </g>
    );
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-gray-300 p-2 rounded shadow text-white text-sm">
          <p>{`${data.label}: ${data.value}`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="size-full bg-[#10151A] ">
      <h2 className="text-white text-lg text-center mb-4">{title}</h2>
      <div className="h-[calc(100%-2rem)]">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 20, right: 20, bottom: 40, left: 20 }}>
            <CartesianGrid
              // strokeDasharray="3 3"
              horizontal={true}
              vertical={false}
              stroke="#333"
            />

            <XAxis
              type="number"
              dataKey="x"
              domain={[0, 100]}
              tick={{ fill: "#fff" }}
              axisLine={false}
              tickLine={false}
              hide
            />
            <YAxis
              type="number"
              dataKey="y"
              domain={[0, 100]}
              tick={{ fill: "#fff" }}
              axisLine={false}
              tickLine={false}
              ticks={[0, 20, 40, 60, 80, 100]}
              reversed
            />
            <Tooltip content={<CustomTooltip />} />
            <Scatter data={data} shape={<CustomShape />} />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default WCABubbleChart;
